/**
 * Ícones da navegação. Traço simples, herdam a cor do texto e ficam legíveis a
 * 22 px no celular. Sem biblioteca externa: são seis formas.
 */
type Props = { tamanho?: number };

const base = (tamanho: number) => ({
  width: tamanho,
  height: tamanho,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
});

export function IconeInicio({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
      <path d="M10 20v-5.5h4V20" />
    </svg>
  );
}

export function IconeAtendimento({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <path d="M20 12a8 8 0 1 1-3.2-6.4" />
      <path d="M4 20l1.6-4.2" />
      <path d="M9 11h6M9 14.5h4" />
    </svg>
  );
}

export function IconeClientes({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20c0-3.3 2.5-5.5 5.5-5.5s5.5 2.2 5.5 5.5" />
      <path d="M16.5 6.2a3 3 0 0 1 0 5.6" />
      <path d="M18 14.8c1.7.7 2.8 2.3 2.8 4.2" />
    </svg>
  );
}

export function IconeAgenda({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
      <path d="M7.5 13.5h3v3h-3z" />
    </svg>
  );
}

export function IconeRetornos({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <path d="M18 15.5V11a6 6 0 1 0-12 0v4.5L4.5 18h15z" />
      <path d="M10 21h4" />
    </svg>
  );
}

export function IconeCatalogo({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <path d="M4 7.5 12 3.5l8 4v9l-8 4-8-4z" />
      <path d="M4 7.5 12 11.5l8-4M12 11.5V20.5" />
    </svg>
  );
}

export function IconeMais({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconeMetricas({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <path d="M4 20V4M4 20h16" />
      <path d="M8 20v-6M12.5 20V8M17 20v-9" />
    </svg>
  );
}

export function IconeConfiguracoes({ tamanho = 22 }: Props) {
  return (
    <svg {...base(tamanho)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.2M12 18.8V21M21 12h-2.2M5.2 12H3M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6M18.4 18.4l-1.6-1.6M7.2 7.2 5.6 5.6" />
    </svg>
  );
}

export function IconeSino({ tamanho = 20 }: Props) {
  return <IconeRetornos tamanho={tamanho} />;
}

/* ------------------------------------------------- ícones da conversa
 *
 * Traço fino, 24×24, herdando a cor do texto — o desenho do WhatsApp. Emoji no
 * lugar de ícone foi o que deixou a barra com cara de rascunho: emoji muda de
 * forma em cada sistema e não aceita a cor da marca.
 */

type P = { size?: number; className?: string };
const traco = (size = 24) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export const IconeMaisFino = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconeEmoji = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <path d="M9 9.5h.01M15 9.5h.01" strokeWidth="2.2" />
  </svg>
);

export const IconeMicrofone = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <rect x="9" y="2.5" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
    <path d="M12 17.5V21M9 21h6" />
  </svg>
);

export const IconeEnviar = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M4 12l16-8-5.5 16-3-6.5L4 12z" />
  </svg>
);

export const IconeCamera = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M3 8.5h3.2L8 6h8l1.8 2.5H21v10a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5v-10z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </svg>
);

export const IconeImagem = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.8" />
    <path d="M4 17l4.5-4.5 3.5 3.5 3-2.5L20 17" />
  </svg>
);

export const IconeDocumento = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </svg>
);

export const IconeContato = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </svg>
);

export const IconeLocalizacao = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.6" />
  </svg>
);

export const IconeLixeira = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" />
  </svg>
);

export const IconeCadeado = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
    <path d="M8.5 10.5V7a3.5 3.5 0 0 1 7 0v3.5" />
  </svg>
);

export const IconeVoltar = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <path d="M15 5l-7 7 7 7" strokeWidth="2" />
  </svg>
);

export const IconeConferido = ({ size = 16, className }: P) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className={className}
  >
    <path d="M1.5 9.5l3 3 6-7" />
    <path d="M7 12.5l3 3 6-7" />
  </svg>
);

export const IconeEnviadoUm = ({ size = 16, className }: P) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className={className}
  >
    <path d="M3 9.5l3.5 3.5L15 4" />
  </svg>
);

export const IconeRelogio = ({ size = 16, className }: P) => (
  <svg {...traco(size)} className={className}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

export const IconeTresPontos = ({ size, className }: P) => (
  <svg {...traco(size)} className={className}>
    <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
  </svg>
);
